import type { IconSize } from '@/components/icons/common'
import { ProgressCircleIcon } from '@/components/icons/progressCircle'
import { cn } from '@/utils/cn'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { Tooltip, TooltipContent, type TooltipProps } from './tooltip'

interface UtilizationTooltipProps {
  totalSupplied: string
  availableLiquidity: string
  utilizationRate?: number
  size?: IconSize
}

type Props = UtilizationTooltipProps & Pick<TooltipProps, 'className' | 'tabIndex'>

export const UtilizationTooltip = ({
  totalSupplied,
  availableLiquidity,
  utilizationRate,
  size = 24,
  className,
  tabIndex,
}: Props) => {
  const totalSuppliedBig = BigInt(totalSupplied)
  if (totalSuppliedBig === 0n) return null

  const usedPercent = Number(((totalSuppliedBig - BigInt(availableLiquidity)) * 100n) / totalSuppliedBig)
  const normalizedUsedPercent = Math.max(0, Math.min(100, usedPercent))
  const tooltipPercentage =
    utilizationRate !== undefined ? formatDecimalToPercentage(utilizationRate) : `${usedPercent}%`

  return (
    <Tooltip
      tabIndex={tabIndex}
      className={cn(
        'relative z-[1] flex min-h-6 w-fit min-w-6 items-center justify-center rounded-full border border-transparent bg-transparent',
        'hocus:border-border hocus:bg-surface',
        className
      )}
    >
      <ProgressCircleIcon progress={normalizedUsedPercent} size={size} className="text-accent" />
      <TooltipContent side="top" align="start">
        <p className="font-semibold">Market utilization is {tooltipPercentage}</p>
      </TooltipContent>
    </Tooltip>
  )
}
